/**
 * 工厂资源节点管理 — 独立页面入口
 * 支持三种视图：卡片 / 表格 / 树
 */
import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  WxbSearchInput,
  WxbSegmented,
  WxbButton,
  WxbPopconfirm,
  WxbTree,
  WxbTag,
  WxbAlert,
  WxbSpinner,
  WxbCheckbox,
  WxbStatusBadge,
} from '../components/wxb-ui';
import { useAuth } from '../contexts/AuthContext';
import {
  useEquipmentManager,
  type SystemTypeFilter,
  type ViewMode,
} from '../components/EquipmentManagement/useEquipmentManager';
import EquipmentCardView from '../components/EquipmentManagement/EquipmentCardView';
import EquipmentTableView from '../components/EquipmentManagement/EquipmentTableView';
import EquipmentDetailDrawer from '../components/EquipmentManagement/EquipmentDetailDrawer';
import EquipmentEditModal from '../components/EquipmentManagement/EquipmentEditModal';
import EquipmentCatalogDrawer from '../components/EquipmentManagement/EquipmentCatalogDrawer';
import EquipmentImportModal from '../components/EquipmentManagement/EquipmentImportModal';
import type { FormMode } from '../components/EquipmentManagement/EquipmentEditModal';
import type {
  ResourceNode,
  ResourceNodeClass,
  ResourceNodeSchedulingProfilePayload,
} from '../components/ProcessTemplateV2/types';
import {
  NODE_CLASS_LABEL,
  NODE_CLASS_COLOR,
} from '../components/EquipmentManagement/resourceNodeConstants';
import './EquipmentManagementPage.css';

/* ── Tree data builder ── */

const toTreeData = (nodes: ResourceNode[]): any[] =>
  nodes.map((node) => ({
    key: node.id,
    title: (
      <span className="equip-tree-title">
        <WxbTag color={NODE_CLASS_COLOR[node.nodeClass]}>{NODE_CLASS_LABEL[node.nodeClass]}</WxbTag>
        <span className="equip-tree-name">{node.nodeName}</span>
        {node.nodeSubtype && <WxbTag>{node.nodeSubtype}</WxbTag>}
        {!node.isActive && <WxbTag color="red">停用</WxbTag>}
      </span>
    ),
    children: toTreeData(node.children),
  }));

/* ── Page Component ── */

const EquipmentManagementPage: React.FC = () => {
  const mgr = useEquipmentManager();
  const { user, hasPermission } = useAuth();
  const canWrite = !user || hasPermission('MASTER_RESOURCE_WRITE');
  const canOperate = !user || hasPermission('MASTER_RESOURCE_OPERATE');

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailStartsEditing, setDetailStartsEditing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create-root');
  const [preferredChildClass, setPreferredChildClass] = useState<ResourceNodeClass | null>(null);
  const [catalogDrawerOpen, setCatalogDrawerOpen] = useState(false);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [importModalOpen, setImportModalOpen] = useState(false);

  /* ── Handlers ──────────────────────────────────────── */
  const handleSelectNode = useCallback(
    (nodeId: number) => {
      mgr.setSelectedNodeId(nodeId);
      setDetailStartsEditing(false);
      setDetailModalOpen(true);
    },
    [mgr],
  );

  const handleEdit = useCallback((node: ResourceNode) => {
    mgr.setSelectedNodeId(node.id);
    setDetailStartsEditing(true);
    setDetailModalOpen(true);
  }, [mgr]);

  const handleCreateRoot = useCallback(() => {
    setPreferredChildClass(null);
    setFormMode('create-root');
    setEditModalOpen(true);
  }, []);

  const handleCreateChild = useCallback((parent: ResourceNode, preferredNodeClass?: ResourceNodeClass) => {
    setDetailModalOpen(false);
    mgr.setSelectedNodeId(parent.id);
    setPreferredChildClass(preferredNodeClass ?? null);
    setFormMode('create-child');
    setEditModalOpen(true);
  }, [mgr]);

  const handleDelete = useCallback(
    async (node: ResourceNode) => {
      if (node.childCount > 0) {
        message.warning('该节点有子节点，请先删除子节点');
        return;
      }
      try {
        await mgr.deleteNode(node.id);
        setDetailModalOpen(false);
        message.success('节点已删除');
      } catch (err: any) {
        message.error(err?.response?.data?.error || '删除失败');
      }
    },
    [mgr],
  );

  const handleToggleActive = useCallback(
    async (node: ResourceNode) => {
      try {
        await mgr.toggleActive(node.id, !node.isActive);
        message.success(node.isActive ? '节点已停用' : '节点已启用');
      } catch {
        message.error('操作失败');
      }
    },
    [mgr],
  );

  const handleBatchToggleActive = useCallback(
    async (ids: number[], isActive: boolean) => {
      try {
        await mgr.batchToggleActive(ids, isActive);
        message.success(`已批量${isActive ? '启用' : '停用'} ${ids.length} 个节点`);
      } catch {
        message.error('批量操作失败');
      }
    },
    [mgr],
  );

  const handleBatchDelete = useCallback(
    async (ids: number[]) => {
      try {
        await mgr.batchDelete(ids);
        message.success(`已批量删除 ${ids.length} 个节点`);
      } catch (err: any) {
        message.error(err?.message || '批量删除失败');
      }
    },
    [mgr],
  );

  const handleUpdateSchedulingProfile = useCallback(
    async (nodeId: number, payload: ResourceNodeSchedulingProfilePayload) => {
      try {
        await mgr.updateSchedulingProfile(nodeId, payload);
        message.success('排产设置已保存');
      } catch (err: any) {
        message.error(err?.response?.data?.error || '排产设置保存失败');
        throw err;
      }
    },
    [mgr],
  );

  const handleExport = useCallback(() => {
    mgr.exportBackup();
    message.success('节点数据已导出');
  }, [mgr]);

  const handleClearRebuild = useCallback(async () => {
    try {
      await mgr.clearForRebuild();
      message.success('节点树已清空');
    } catch (err: any) {
      message.error(err?.response?.data?.error || '清空失败');
    }
  }, [mgr]);

  const handleTreeDrop = useCallback(
    async (info: any) => {
      const dragNodeId = Number(info.dragNode.key);
      const dropNodeId = Number(info.node.key);
      const dropNode = mgr.allNodes.find((n) => n.id === dropNodeId);
      if (!dropNode || dragNodeId === dropNodeId) return;

      try {
        if (info.dropToGap) {
          await mgr.moveNode(dragNodeId, dropNode.parentId ?? null, dropNode.sortOrder);
        } else {
          await mgr.moveNode(dragNodeId, dropNode.id);
        }
        message.success('节点层级已更新');
      } catch (err: any) {
        message.error(err?.response?.data?.error || '移动节点失败');
      }
    },
    [mgr],
  );

  /* ── Loading / Error ── */
  if (mgr.loading) {
    return (
      <div className="equip-page equip-page-loading">
        <WxbSpinner />
        <span>加载资源节点...</span>
      </div>
    );
  }

  if (mgr.errorMessage) {
    return (
      <div className="equip-page">
        <WxbAlert variant="error">{mgr.errorMessage}</WxbAlert>
      </div>
    );
  }

  /* ── Filter options ────────────────────────────────── */
  const systemTypeOptions = [
    { label: `全部 (${mgr.nodeStats.totalCount})`, value: 'ALL' },
    { label: `SUS (${mgr.nodeStats.susByType})`, value: 'SUS' },
    { label: `SS (${mgr.nodeStats.ssByType})`, value: 'SS' },
    { label: `虚拟 (${mgr.nodeStats.virtualByType})`, value: 'VIRTUAL' },
  ];

  const viewModeOptions = [
    { label: '房间资源板', value: 'card' },
    { label: '层级台账', value: 'table' },
    { label: '层级调整', value: 'tree' },
  ];

  /* ── Tree data ── */
  const treeData = toTreeData(mgr.filteredTree);

  /* ── Render ────────────────────────────────────────── */
  return (
    <div className="equip-page">
      {/* Page Header */}
      <div className="equip-page-header">
        <div className="equip-page-title-area">
          <h1 className="equip-page-title">工厂资源节点管理</h1>
          <span className="equip-page-breadcrumb">基础数据 / 资源节点管理</span>
        </div>
        <div className="equip-page-actions">
          <WxbButton variant="secondary" size="sm" onClick={() => setCatalogDrawerOpen(true)}>
            设备类型与型号
          </WxbButton>
          {canWrite && (
            <>
              <WxbButton variant="secondary" size="sm" onClick={() => setImportModalOpen(true)}>
                批量导入
              </WxbButton>
              <WxbButton variant="primary" size="sm" onClick={handleCreateRoot}>
                新建厂区
              </WxbButton>
            </>
          )}
          <WxbButton variant="ghost" size="sm" onClick={handleExport}>
            导出
          </WxbButton>
          {canOperate && (
            <WxbPopconfirm
              title="清空并重建节点树？"
              description="此操作将清空所有节点、模板绑定和 CIP 关系。不可撤销。"
              onConfirm={handleClearRebuild}
            >
              <WxbButton variant="danger" size="sm">
                清空重建
              </WxbButton>
            </WxbPopconfirm>
          )}
          <WxbButton variant="ghost" size="sm" onClick={mgr.refresh}>
            刷新
          </WxbButton>
        </div>
      </div>

      {/* Compact summary */}
      <div className="equip-summary-bar">
        <div className="equip-summary-items">
          <span className="equip-summary-item">
            <span className="equip-summary-value">{mgr.nodeStats.totalCount}</span> 个节点
          </span>
          <span className="equip-summary-item">
            <span className="equip-summary-value">{mgr.nodeStats.roomCount}</span> 个房间
          </span>
          <span className="equip-summary-item">
            <span className="equip-summary-value">
              {mgr.nodeStats.mappedResourceCount} / {mgr.nodeStats.bindableCount}
            </span>
            已配置排产
          </span>
          <span className={`equip-summary-item ${mgr.nodeStats.issueCount > 0 ? 'is-warning' : ''}`}>
            <span className="equip-summary-value">{mgr.nodeStats.issueCount}</span> 项待处理
          </span>
        </div>
        <WxbStatusBadge
          status={mgr.nodeStats.issueCount > 0 ? 'warning' : 'success'}
          label={mgr.nodeStats.issueCount > 0 ? '资源数据需完善' : '资源数据完整'}
          variant="dot"
          size="sm"
        />
      </div>

      {/* Toolbar */}
      <div className="equip-toolbar">
        <WxbSearchInput
          value={mgr.search}
          onChange={mgr.setSearch}
          placeholder="搜索名称、编号、资源..."
        />
        <WxbSegmented
          options={systemTypeOptions}
          value={mgr.systemTypeFilter}
          onChange={(v) => mgr.setSystemTypeFilter(v as SystemTypeFilter)}
          size="sm"
        />
        <WxbCheckbox
          checked={mgr.issuesOnly}
          onChange={mgr.setIssuesOnly}
          className="equip-issue-filter"
        >
          只看待处理
        </WxbCheckbox>
        <span className="equip-toolbar-spacer" />
        <WxbSegmented
          options={viewModeOptions}
          value={mgr.viewMode}
          onChange={(v) => mgr.setViewMode(v as ViewMode)}
          size="sm"
        />
      </div>

      {/* Content */}
      {mgr.viewMode === 'card' && (
        <EquipmentCardView
          nodes={mgr.filteredTree}
          selectedNodeId={mgr.selectedNodeId}
          canWrite={canWrite}
          onSelect={handleSelectNode}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          onCreateChild={handleCreateChild}
        />
      )}

      {mgr.viewMode === 'table' && (
        <EquipmentTableView
          nodes={mgr.filteredTree}
          selectedNodeId={mgr.selectedNodeId}
          selectedIds={mgr.selectedIds}
          canWrite={canWrite}
          onSelect={handleSelectNode}
          onSelectionChange={mgr.setSelectedIds}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          onCreateChild={handleCreateChild}
          onBatchToggleActive={handleBatchToggleActive}
          onBatchDelete={handleBatchDelete}
        />
      )}

      {mgr.viewMode === 'tree' && (
        <div className="equip-tree-view">
          <WxbTree
            treeData={treeData}
            selectedKeys={mgr.selectedNodeId ? [mgr.selectedNodeId] : []}
            onSelect={(keys) => {
              if (keys.length) handleSelectNode(Number(keys[0]));
            }}
            draggable={canWrite}
            onDrop={canWrite ? handleTreeDrop : undefined}
            defaultExpandAll
            showLine
          />
        </div>
      )}

      {/* Detail Modal */}
      <EquipmentDetailDrawer
        node={mgr.selectedNode}
        open={detailModalOpen}
        nodePath={mgr.selectedNodePath}
        allNodes={mgr.allNodes}
        childBlueprintsCount={mgr.childBlueprints.length}
        catalogRevision={catalogRevision}
        canWrite={canWrite}
        startInEditMode={detailStartsEditing}
        onClose={() => {
          setDetailStartsEditing(false);
          setDetailModalOpen(false);
        }}
        onCreateChild={handleCreateChild}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        onUpdateNode={mgr.updateNode}
        onUpdateSchedulingProfile={handleUpdateSchedulingProfile}
      />

      {/* Edit/Create Modal */}
      <EquipmentEditModal
        open={editModalOpen}
        mode={formMode}
        editingNode={null}
        parentNode={formMode === 'create-child' ? mgr.selectedNode : null}
        allNodes={mgr.allNodes}
        initialChildClass={preferredChildClass}
        catalogRevision={catalogRevision}
        onCancel={() => setEditModalOpen(false)}
        onCreate={mgr.createNode}
        onUpdate={mgr.updateNode}
      />

      <EquipmentCatalogDrawer
        open={catalogDrawerOpen}
        canWrite={canWrite}
        onClose={() => setCatalogDrawerOpen(false)}
        onCatalogChanged={() => setCatalogRevision((current) => current + 1)}
      />

      <EquipmentImportModal
        open={importModalOpen}
        canWrite={canWrite}
        onClose={() => setImportModalOpen(false)}
        onImported={mgr.refresh}
      />
    </div>
  );
};

export default EquipmentManagementPage;
